/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */
import React, { useEffect } from "react";
import { View } from "react-native";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
} from "@material-ui/core";
import Sticky from "react-stickynode";

import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store/types";
import { ActionCreators } from "actions";

import { LoadingDots, Text } from "UpduoLibrary";
import {
  skaleWhite,
  upduoPrimaryBlack,
  upduoMediumLightGray,
} from "config/UpduoColors";
import useActivity, { SessionsCollection } from "./useActivity";
import TranscriptionSidePane from "./TranscriptionSidePane";
import ActivityRow from "./ActivityRow";
import { SkaleSessionType } from "graphql/internal/generated";

interface ActivityProps {
  showIncompleteSessions: boolean;
  sessionsLoading: boolean;
  activityObject: SessionsCollection | undefined;
  onUpdateSession: (
    sessionId: string,
    changes: Partial<
      Pick<SkaleSessionType, "isCancelled" | "allowSharing" | "masteryEdited">
    >
  ) => void;
}

export default function Activity({
  showIncompleteSessions,
  sessionsLoading,
  activityObject,
  onUpdateSession,
}: ActivityProps) {
  const { expandedColumnTitleArray, filteredActivityResults, showTranscript } =
    useActivity(showIncompleteSessions, activityObject);
  const transcriptionObject = useSelector(
    (state: RootState) => state.transcriptionObject
  );
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(ActionCreators.setTranscriptionObject(null));
  }, []);

  const expandedColumnHeaders = expandedColumnTitleArray.map(
    (item, itemIndex) => (
      <TableCell
        key={itemIndex + 1}
        align={/LENGTH|VIDEO|TRANSCRIPT/.exec(item) ? "center" : "left"}
      >
        <View style={{ height: 40, justifyContent: "center" }}>
          <Text style={{ color: upduoMediumLightGray, fontSize: 14 }}>
            {item}
          </Text>
        </View>
      </TableCell>
    )
  );

  return (
    <View style={{ flexDirection: "row" }}>
      <TableContainer
        style={{
          width: transcriptionObject ? "60%" : "100%",
          backgroundColor: skaleWhite,
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
        }}
      >
        <Table style={{ marginLeft: 30, marginRight: 30 }}>
          <TableHead>
            <TableRow style={{ backgroundColor: skaleWhite }}>
              {expandedColumnHeaders}
            </TableRow>
          </TableHead>
          {activityObject?.totalCount === 0 ? (
            <TableBody style={{ backgroundColor: skaleWhite }}>
              <TableRow>
                <TableCell colSpan={7}>
                  <View
                    style={{
                      justifyContent: "center",
                      alignItems: "center",
                      height: 55,
                      width: "100%",
                    }}
                  >
                    <Text
                      style={{
                        fontStyle: "normal",
                        fontWeight: "400",
                        fontSize: 16,
                        color: upduoPrimaryBlack,
                      }}
                    >
                      {"No sessions match your filters"}
                    </Text>
                  </View>
                </TableCell>
              </TableRow>
            </TableBody>
          ) : activityObject && activityObject.totalCount > 0 ? (
            <TableBody style={{ backgroundColor: skaleWhite }}>
              {sessionsLoading ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <View
                      style={{
                        justifyContent: "center",
                        alignItems: "center",
                        height: 55,
                        width: "100%",
                      }}
                    >
                      <LoadingDots />
                    </View>
                  </TableCell>
                </TableRow>
              ) : activityObject ? (
                filteredActivityResults?.map((session, i) => (
                  <ActivityRow
                    key={session.id}
                    itemIndex={i}
                    rowData={session}
                    showTranscription={showTranscript}
                    onUpdateSession={(changes) =>
                      onUpdateSession(session.id, changes)
                    }
                  />
                ))
              ) : null}
            </TableBody>
          ) : null}
        </Table>
      </TableContainer>
      {transcriptionObject && (
        <View style={{ width: "41%" }}>
          <Sticky enabled={true}>
            <TranscriptionSidePane />
          </Sticky>
        </View>
      )}
    </View>
  );
}
